import { Box } from 'reflexbox';
import styled from 'styled-components';

import { Button } from '~/components';
import { healthLevelColor } from '~/components/HealthIcon';
import { colors } from '~/styles/colors';
import { ExportWorksheetIcon } from 'icons';
import { CalendarIcon } from '@radix-ui/react-icons';

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;
`;

export const SelectedStatusText = styled.span<{ health }>(
  ({ health }) => `
  margin-top: 12px;
  position: absolute;
  font-weight: bold;
  color:  ${healthLevelColor(health)};
`,
);

export const StatusText = styled.p<{ isBold? }>(
  ({ isBold }) => `
  margin-bottom: ${isBold ? 0 : '1em'};
  margin-top: ${isBold ? '24px' : 0};
  color: ${isBold ? colors.Grey300 : colors.Grey400};
  font-weight: ${isBold ? 'bold' : 'normal'};
`,
);

export const StyledBox = styled(Box)(
  (props: { health, selected }) => `
  div {
    width: ${props.selected ? '48px' : '32px'};
    height: ${props.selected ? '48px' : '32px'};
    background-color: ${healthLevelColor(props.health)};
  }
  svg {
    width: ${props.selected ? '28px' : '20px'};
    height: ${props.selected ? '28px' : '20px'};
  }
`,
);

export const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  border-radius: 8px;
`;

export const CardWrapper = styled.div`
  padding: 32px 16px;
  min-height: 118px;
  border-radius: 16px;
  background-color: ${colors.White};
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.24), 0 3px 12px rgba(0, 0, 0, 0.12);
  @media (min-width: 992px) {
    padding: 32px;
  }
`;

export const CardTitle = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 1.5em;
  color: ${colors.Grey400};
`;

export const ContainerHealths = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 50px;

  @media (max-width: 1024px) {
    margin-bottom: 18px;
  }
`;

export const CustomButton = styled(Button)`
  min-height: 24px;
  max-width: 110px;
  display: inline-block;
  justify-content: center;
  padding-left: 5px;
  padding-right: 5px;
  margin-right: 10px;
`;

type CustomButtonProps = {
  selected: boolean;
}

export const GreenButton = styled(CustomButton)<CustomButtonProps>`
  background-color: rgba(41, 150, 86, 1);
  border-color: ${({ selected }) => (selected ? 'black' : 'rgba(41, 150, 86, 1)')};
  color: ${({ selected }) => (selected ? 'black' : colors.White)};
  &:hover {
    background-color: rgba(41, 150, 86, 0.8);
  }
`;

type YellowButtonProps = {
  selected: boolean;
}

export const YellowButton = styled(CustomButton)<YellowButtonProps>`
  background-color: rgba(234, 203, 62, 1);
  border-color: ${({ selected }) => (selected ? 'black' : 'rgba(234, 203, 62, 1)')};
  color: ${({ selected }) => (selected ? 'black' : colors.White)};
  &:hover {
    background-color: rgba(234, 203, 62, 0.8);
  }
`;

type OrangeButtonProps = {
  selected: boolean;
}

export const OrangeButton = styled(CustomButton)<OrangeButtonProps>`
  background-color: rgba(229, 149, 77, 1);
  border-color: ${({ selected }) => (selected ? 'black' : 'rgba(229, 149, 77, 1)')};
  color: ${({ selected }) => (selected ? 'black' : colors.White)};
  &:hover {
    background-color: rgba(229, 149, 77, 0.8);
  }
`;

type RedButtonProps = {
  selected: boolean;
}

export const RedButton = styled(CustomButton)<RedButtonProps>`
  background-color: rgba(217, 68, 56, 1);
  border-color: ${({ selected }) => (selected ? 'black' : 'rgba(217, 68, 56, 1)')};
  color: ${({ selected }) => (selected ? 'black' : colors.White)};
  &:hover {
    background-color: rgba(217, 68, 56, 0.8);
  }
`;

type GreyButtonProps = {
  selected: boolean;
}

export const GreyButton = styled(CustomButton)<GreyButtonProps>`
  background-color: rgba(171, 176, 180, 1);
  border-color: ${({ selected }) => (selected ? 'black' : 'rgba(171, 176, 180, 1)')};
  color: ${({ selected }) => (selected ? 'black' : colors.White)};
  &:hover {
    background-color: rgba(171, 176, 180, 0.8);
  }
`;

type DarkGreyButtonProps = {
  selected: boolean;
}

export const DarkGreyButton = styled(CustomButton)<DarkGreyButtonProps>`
  background-color: rgba(85, 85, 85, 1);
  border-color: ${({ selected }) => (selected ? 'black' : 'rgba(171, 176, 180, 1)')};
  color: ${({ selected }) => (selected ? 'black' : colors.White)};
  max-width: 130px;
  &:hover {
    background-color: rgba(85, 85, 85, 0.8);
  }
`;

export const SaveButton = styled(Button)`
  min-height: 24px;
  max-width: 120px;
  display: flex;
  justify-content: center;
`;

export const DataCentered = styled.td<{ thereIsValue? }>`
  color: ${({ thereIsValue }) => (thereIsValue ? colors.LightBlue : colors.DarkGrey)};
  ${({ thereIsValue }) => (thereIsValue ? 'cursor: pointer;' : '')}
  text-decoration: ${({ thereIsValue }) => (thereIsValue ? 'underline' : 'none')};
  padding: 0 10px;
  text-align: center
`;

interface ButtonProps {
  width?: string;
  height?: string;
}

export const ButtonCustom = styled.button<ButtonProps>`
  cursor: pointer;
  padding: 12px;
  color: ${colors.White};
  transition: all 0.3s ease-in-out;
  border-radius: 4px;
  background-color: ${colors.brand.primary.dark};
  border-color: ${colors.brand.primary.dark};
  font-weight: 500;
  width: 200px;

  :hover {
    background-color: ${colors.brand.primary.base};
    border-color: ${colors.brand.primary.base};
  }

  @media (min-width: 992px) {
    width: ${({ width }) => width || '200px'};
    height: ${({ height }) => height || '50px'};
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    line-height: 16px;
  }
`;

export const ContainerModal = styled.div`
  padding: 10px;
  position: relative;
`;

export const LabelModal = styled.label`
  font-size: 14px;
`;

export const CellModal = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  gap: 10px;
`;

export const InputModal = styled.input`
  height: 48px;
  border-radius: 8px;
  border: 2px solid ${colors.Grey};
  padding: 0 10px;
  font-size: 14px;
  &:focus {
    outline: none;
    border: 1px solid ${colors.Blue300};
    box-shadow: 0 0 0 3px rgba(41, 128, 185, 0.2);
  }
`;

export const ContentDate = styled.div`
  /* Input de data */
  position: relative;
  border: 2px solid ${colors.Grey};
  border-radius: 8px;
  height: 48px;
  width: 226px;
  background-color: white;
  .react-datepicker__month-text--keyboard-selected {
    background-color: ${colors.BlueSecondary};
    color: white;
  }
  .CalendarMonth_caption {
    text-transform: capitalize;
  }
  .react-datepicker__triangle {
    left: -130px !important;
  }
  .react-datepicker__header {
    background-color: white;
    border-bottom: none;
  }

  .react-datepicker-popper {
    z-index: 99;
  }
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::after {
    display: none;
  }

  .react-datepicker-popper[data-placement^=bottom] .react-datepicker__triangle::before {
    display: none;
  }
  .react-datepicker-wrapper {
    display: block;
    .react-datepicker__input-container {
      top: -5px;
      input[type="text"] {
        border: none;
        font-size: 12px;
        outline: none;
        line-height: 19px;
        padding: 0 40px 0 14px;
        color: #464555;
        background: transparent;
      }
    }
  }
  .SingleDatePicker {
    display: block;
    position: initial;
  }
  .SingleDatePickerInput {
    display: block;
    position: relative;
    border: none;
    background: transparent;
    z-index: 10;
    top: -30px;
    .DateInput {
      display: block;
      position: relative;
      width: 100%;
      background: transparent;
      /* Valor que aparece no input */
      .DateInput_input {
        position: relative;
        top: 25px;
        outline: none;
        font-size: 12px;
        line-height: 19px;
        padding: 0 40px 0 14px;
        font-weight: 300;
        color: ${colors.Grey400};
        width: 100%;
        background: transparent;
        border: none;
      }
      .DateInput_input__focused {
        border: none;
      }
    }
  }
  .DateRangePickerInput {
    .DateRangePickerInput_arrow {
      width: 60px;
    }
    .DateInput {
      .DateInput_input {
        outline: none;
        font-size: 12px;
        line-height: 19px;
        padding: 0 40px 0 14px;
        color: ${colors.Grey400};
      }
      .DateInput_input__focused {
        border: none;
      }
    }
  }
  .DateInput_fang {
    z-index: 1;
  }
  .SingleDatePicker_picker {
    width: 100%;
  }
  .CalendarDay__selected,
  .CalendarDay__selected:active {
    background: ${colors.BlueSecondary};
    border: 1px double ${colors.BlueSecondary};
    color: ${colors.White};
    font-weight: bold;
  }
  .CalendarDay__selected:hover {
    background: ${colors.BlueSecondary};
    border: 1px double ${colors.BlueSecondary};
    color: ${colors.Black};
  }
  .DayPickerKeyboardShortcuts_show__bottomRight::before {
    border-right: 33px solid ${colors.BlueSecondary};
    display: none;
  }
  .DayPickerKeyboardShortcuts_show__bottomRight:hover::before {
    border-right: 33px solid ${colors.BlueSecondary};
  }
`;

export const DateLabel = styled.span`
  transition: all 0.2s;
  margin-left: 14px;
  margin-right: 5px;

  font-style: normal;
  font-weight: 700;
  font-size: 11px;
  line-height: 13px;
  color: ${colors.brand.primary.dark};
`;

export const StyledCalendarIcon = styled(CalendarIcon)`
  position: absolute;
  top: 17px;
  right: 16px;
`;

export const PdfListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid ${colors.Grey};
  border-radius: 4px;
  margin-bottom: 4px;
`;

export const PdfDownloadButton = styled.button`
  color: ${colors.BlueSecondary};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
  cursor: pointer;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
`;

export const ButtonAddImage = styled.label`
  color: ${colors.BlueSecondary};
  border: 1px solid ${colors.BlueSecondary};
  border-radius: 4px;
  background-color: ${colors.White};
  padding: 0 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 33px;
  width: 100%;
  font-weight: 600;
  gap: 6px;
  &:hover {
    cursor: pointer;
    background-color: ${colors.Blue100};
    border: 1px solid transparent;
  }
`;

export const ExportWorksheet = styled(ExportWorksheetIcon)(
  () => `
  color: #363BC4
`,
);

export const Text = styled.text`
color: ${colors.Black};
`;

export const BtnExport = styled.div`
  cursor: pointer;
  color: #363BC4;
  padding: 14px 20px;
  border: 1px solid ${colors.GreyLight};
  margin-left: 10px;
  font-weight: 400;
  border-radius: 12px;
  height: 41px;
  display: flex;
  text-align: center;
  align-items: center;
  :hover {
    color: ${colors.White}
    background-color: #363BC4
    ${Text} {
      color: ${colors.White}
    }
  }
`;

export const ModalTitle = styled.span`
  font-weight: bold;
  font-size: 1.25em;
  line-height: 27px;
  color: ${colors.Grey400};
`;

export const MaintenanceValidationHealthIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #3180C6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  flex-shrink: 0;
`;

export const MaintenanceValidationStatusLabel = styled.span`
  margin-top: 12px;
  position: absolute;
  font-weight: bold;
  color: #3180C6;
`;

export const MaintenanceValidationNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #B3D4F0;
  background: #F2F7FD;
`;

export const MaintenanceValidationNoteTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #3180C6;
  margin-bottom: 3px;
`;

export const MaintenanceValidationNoteBody = styled.div`
  font-size: 12px;
  color: #525866;
  line-height: 1.55;
`;
